import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import moment from "moment-timezone";

import {
  RotaButtonIcon,
  RotaCalendar,
  RotaChip,
  iconNew as RotaIcon,
} from "@teamrota/rota-design";

import ShiftStream from "../..";
import Pagination from "../../components/pagination";
import Icon, { ICON_NAMES as Icons } from "~/src/components/icon";
import { formatDateMonthTimeShort } from "~/src/utils/formatting";

import { useVenues } from "../../graphql/use-venues";
import { useClients } from "../../graphql/use-clients";
import { useRoles } from "../../graphql/use-roles";
import { useAccounts } from "../../graphql/use-accounts";

import { cleanAddress, getProviderLabel } from "../../utils";

import {
  CalendarWrapper,
  CheckboxLabel,
  FilterGrid,
  FilterGridRow,
  FilterLinks,
  FilterSelect,
  FilterSlot,
  FooterControls,
  FooterLabel,
  OrderBySelect,
  PaginationSlot,
  RefreshButton,
} from "../styles";

import { StreamOrder, useStreams } from "./graphql/use-streams";
import { useEmail } from "./graphql/use-email";
import Table from "./Table";
import {
  getDefaultFilterState,
  getSavedFilterState,
  saveFilterState,
} from "./filters";

const { ChevronLeft } = RotaIcon;

export const COL_WIDTHS = [
  "11vw",
  "14vw",
  "14vw",
  "14vw",
  "11vw",
  "16vw",
  "6vw",
];

export const Streams = () => {
  const history = useHistory();

  const { streamId, emailId, accountId } = useParams<{
    accountId?: string;
    emailId?: string;
    streamId?: string;
  }>();

  const [filters, setFilters] = useState(getSavedFilterState());
  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState<StreamOrder>("ID");

  useEffect(() => saveFilterState(filters), [filters]);

  useEffect(() => {
    if (accountId) {
      setFilters({ ...filters, account: parseInt(accountId) });
    }
  }, [accountId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    streamId,
    emailId,
    filters.account,
    filters.client,
    filters.role,
    filters.venue,
    filters.includeFilled,
    orderBy,
  ]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, venue: "all", role: "all" }));
  }, [filters.client, streamId, emailId]);

  const { accounts } = useAccounts();

  useEffect(() => {
    const currentProviderAccounts =
      filters.provider === "all"
        ? accounts
        : accounts.filter(
            (account: any) => account.provider === filters.provider,
          );

    if (
      filters.account !== "all" &&
      !currentProviderAccounts.some(
        (account: any) => account.id === filters.account,
      )
    ) {
      setFilters((prev) => ({ ...prev, account: "all" }));
    }
  }, [accounts, filters.provider, filters.account]);

  const { clients } = useClients();

  const { roles } = useRoles({
    clientIds: filters.client !== "all" ? [filters.client] : null,
  });

  const { venues } = useVenues({
    clientIds: filters.client !== "all" ? [filters.client] : null,
  });

  // if there's an email filter, fetch it to get related stream ids
  const { email } = useEmail({
    id: emailId ? parseInt(emailId) : null,
  });

  let idsFilter = null;
  if (email) idsFilter = email.streamIds.map((x: any) => parseInt(x));
  if (streamId) idsFilter = [parseInt(streamId)];

  const { streams, total, totalPages, loading, refetch } = useStreams(
    {
      ids: idsFilter,
      accountId: filters.account !== "all" ? filters.account : null,
      sourceAccountId: filters.client !== "all" ? filters.client : null,
      roleId: filters.role !== "all" ? filters.role : null,
      venueId: filters.venue !== "all" ? filters.venue : null,
      startDate: filters.startDate,
      endDate: filters.endDate,
      includeFilled: filters.includeFilled,
      orderBy,
    },
    currentPage,
  );

  useEffect(() => {
    if (!loading && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, loading]);

  const handleDateRangeChange = ({
    startDate,
    endDate,
  }: {
    startDate?: Date;
    endDate?: Date;
  }) => {
    console.log("handleDateRangeChange", { startDate, endDate });

    let startMoment = startDate ? moment(startDate).startOf("day") : null;
    let endMoment = endDate ? moment(endDate).startOf("day") : null;

    if (startMoment && endMoment) {
      if (startMoment.isAfter(endMoment)) {
        setFilters({ ...filters, endDate: endMoment, startDate: startMoment });
      } else {
        setFilters({ ...filters, startDate: startMoment, endDate: endMoment });
      }
    }
  };

  const handleUnfilledToday = () => {
    const todayStart = moment().startOf("day");
    const todayEnd = moment().endOf("day");

    setFilters({
      ...filters,
      startDate: todayStart,
      endDate: todayEnd,
      includeFilled: false,
    });
  };

  const handleUnfilledTomorrow = () => {
    const tomorrowStart = moment().add(1, "day").startOf("day");
    const tomorrowEnd = moment().add(1, "day").endOf("day");

    setFilters({
      ...filters,
      startDate: tomorrowStart,
      endDate: tomorrowEnd,
      includeFilled: false,
    });
  };

  const handleClearFilters = () => {
    setFilters(getDefaultFilterState());
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    const scrollBody = document.querySelector(".streams-table-scroll-body");
    if (scrollBody instanceof HTMLElement) {
      scrollBody.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  const handleOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrderBy(e.target.value as StreamOrder);
    setCurrentPage(1);

    const scrollBody = document.querySelector(".streams-table-scroll-body");
    if (scrollBody instanceof HTMLElement) {
      scrollBody.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <ShiftStream>
      <FilterLinks style={{ marginBottom: "8px" }}>
        Quick Links{" "}
        <a href="#" onClick={handleUnfilledToday}>
          Unfilled Today
        </a>
        <a href="#" onClick={handleUnfilledTomorrow}>
          Unfilled Tomorrow
        </a>
        <CheckboxLabel htmlFor="streams-include-filled">
          <input
            id="streams-include-filled"
            type="checkbox"
            checked={filters.includeFilled}
            onChange={(e) =>
              setFilters({ ...filters, includeFilled: e.target.checked })
            }
          />
          Include Full
        </CheckboxLabel>
        <a
          href="#"
          onClick={handleClearFilters}
          style={{ float: "right", padding: "12px 0" }}
        >
          Clear Filters
        </a>
      </FilterLinks>

      {email ? (
        <div style={{ width: "87vw", marginBottom: 16 }}>
          <RotaButtonIcon onClick={() => history.push("/shiftstream/emails")}>
            <ChevronLeft />
          </RotaButtonIcon>

          <RotaChip
            chipText={`email #${email.id} from ${cleanAddress(
              email.sender,
            )} ➡️ ${cleanAddress(
              email.mailboxAddress,
            )} at ${formatDateMonthTimeShort(email.receivedAt)}`}
            onClose={() => history.push("/shiftstream/streams")}
            key={email.id}
          />
        </div>
      ) : (
        <FilterGrid>
          <FilterGridRow colWidths={COL_WIDTHS}>
            <FilterSlot>
              <FilterSelect
                id="stream-account-filter"
                value={filters.account ?? "all"}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    account:
                      e.target.value === "all" ? "all" : Number(e.target.value),
                  })
                }
              >
                <option value="all">All Accts</option>
                {accounts.map((account: any) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({getProviderLabel(account.provider)})
                  </option>
                ))}
              </FilterSelect>
            </FilterSlot>

            <FilterSlot>
              <FilterSelect
                id="stream-client-filter"
                value={filters.client ?? "all"}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    client:
                      e.target.value === "all" ? "all" : Number(e.target.value),
                  })
                }
              >
                <option value="all">All Clients</option>
                {clients.map((client: any) => (
                  <option key={client.id} value={client.id}>
                    {client.accountName}
                  </option>
                ))}
              </FilterSelect>
            </FilterSlot>

            <FilterSlot>
              <FilterSelect
                id="stream-venue-filter"
                value={filters.venue ?? "all"}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    venue:
                      e.target.value === "all" ? "all" : Number(e.target.value),
                  })
                }
              >
                <option value="all">All Venues</option>
                {venues.map((venue: any) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}
                  </option>
                ))}
              </FilterSelect>
            </FilterSlot>

            <FilterSlot>
              <FilterSelect
                id="stream-role-filter"
                value={filters.role ?? "all"}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    role:
                      e.target.value === "all" ? "all" : Number(e.target.value),
                  })
                }
              >
                <option value="all">All Roles</option>
                {roles.map((role: any) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </FilterSelect>
            </FilterSlot>

            <FilterSlot>
              <CalendarWrapper>
                <RotaCalendar
                  key={`calendar-${filters.startDate?.toISOString() ?? "null"}-${filters.endDate?.toISOString() ?? "null"}`}
                  isSelectsRange
                  startDate={(filters.startDate?.toDate() ?? null) as any}
                  endDate={filters.endDate?.toDate() ?? null}
                  onChange={handleDateRangeChange}
                />
              </CalendarWrapper>
            </FilterSlot>

            <FilterSlot style={{ justifyContent: "flex-end" }}>
              <RefreshButton
                type="button"
                aria-label="Refresh"
                title="Refresh"
                disabled={loading}
                onClick={handleRefresh}
                style={{ marginLeft: 0 }}
              >
                <Icon
                  name={Icons.REFRESH}
                  size={18}
                  color={
                    loading ? Icon.colors.GREY_DARK : Icon.colors.MAIN_ORANGE
                  }
                  onClick={handleRefresh}
                />
              </RefreshButton>
            </FilterSlot>
          </FilterGridRow>
        </FilterGrid>
      )}

      <Table streams={streams} loading={loading} />

      <FooterControls>
        <FooterLabel>Order By</FooterLabel>
        <OrderBySelect
          id="stream-order-by"
          value={orderBy}
          onChange={handleOrderChange}
        >
          <option value="ID">ID#</option>
          <option value="CLIENT">Client</option>
          <option value="VENUE">Venue</option>
          <option value="ROLE">Role</option>
          <option value="START">Date/Time</option>
        </OrderBySelect>

        <PaginationSlot>
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            total={total}
            onPageChange={handlePageChange}
            disabled={loading}
          />
        </PaginationSlot>
      </FooterControls>
    </ShiftStream>
  );
};
